import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { getNews, peekNewsCache } from "../../services/news.service";
import { mergeEditorialArticles } from "../../content/news";

function formatDate(isoOrString) {
  if (!isoOrString) return "";
  try {
    return new Date(isoOrString).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  } catch {
    return isoOrString;
  }
}

// Shared article-listing grid, used by both the Research page ("Latest
// research") and the Newsroom page — same underlying feed, different
// framing text and permalink base per caller.
export default function ArticleGrid({ basePath = "/news", emptyMessage = "No articles published yet — check back soon." }) {
  const cachedNews = peekNewsCache({ limit: 20 });
  const initialArticles = mergeEditorialArticles(cachedNews?.articles || []);
  const [articles, setArticles] = useState(() => initialArticles);
  const [loading, setLoading] = useState(() => !cachedNews && initialArticles.length === 0);

  useEffect(() => {
    let active = true;
    getNews({ limit: 20 })
      .then((data) => { if (active && data.articles) setArticles(data.articles); })
      .catch(() => { /* leave empty */ })
      .finally(() => { if (active) setLoading(false); });

    return () => { active = false; };
  }, []);

  if (loading) {
    return <p style={{ textAlign: "center", color: "var(--text-dim)", fontSize: 13 }}>Loading…</p>;
  }

  if (articles.length === 0) {
    return <p style={{ color: "var(--text-dim)", fontSize: 14, margin: 0 }}>{emptyMessage}</p>;
  }

  return (
    <div className="grid-2">
      {articles.map((p, i) => (
        <Link key={p.slug || i} to={`${p.editorial ? "/news" : basePath}/${p.slug}`} style={{ textDecoration: "none" }}>
          <div
            className={`reveal reveal-d${(i % 4) + 1} card-dark`}
            style={{
              display: "flex",
              flexDirection: "column",
              padding: "32px 28px",
              cursor: "pointer",
              transition: "border-color .15s, transform .15s",
              height: "100%",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = "var(--accent-dim)";
              e.currentTarget.style.transform = "translateY(-2px)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = "";
              e.currentTarget.style.transform = "";
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 18 }}>
              <span style={{
                fontSize: 10, fontWeight: 600, letterSpacing: ".07em", textTransform: "uppercase",
                color: p.tagColor, background: `${p.tagColor}18`,
                border: `1px solid ${p.tagColor}33`, borderRadius: 999, padding: "3px 9px",
              }}>{p.tag}</span>
              <span style={{ fontSize: 12, color: "var(--text-dim)", marginLeft: "auto" }}>
                {p.date || formatDate(p.createdAt)}
              </span>
            </div>
            <h3 style={{ fontSize: 18, fontWeight: 700, color: "var(--text-primary)", letterSpacing: "-.02em", lineHeight: 1.25, marginBottom: 12 }}>
              {p.title}
            </h3>
            <p style={{ fontSize: 14, color: "var(--text-tertiary)", lineHeight: 1.65, marginBottom: 18 }}>
              {p.excerpt}
            </p>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: "auto" }}>
              <span style={{ fontSize: 12, color: "var(--text-dim)" }}>{p.readTime}</span>
              <span style={{ fontSize: 12, color: "var(--accent)", fontWeight: 500 }}>Read →</span>
            </div>
          </div>
        </Link>
      ))}
    </div>
  );
}
